import React from "react";
import { TTypographyVariant } from "../../../types";
import Flex from "../../Flex";
import { Typography } from "../../Typography";
import { TCheckboxSize } from "../CheckboxBase/helpers";
import Radio from "../Radio";

export interface IProps<TOption, TValue extends string>
    extends Pick<React.HTMLAttributes<HTMLDivElement>, "style" | "className"> {
    options: readonly TOption[];
    getOptionValue: (option: TOption, index: number, options: readonly TOption[]) => TValue;
    getOptionLabel: (option: TOption, index: number, options: readonly TOption[]) => React.ReactNode;
    getHelperText?: (option: TOption, index: number, options: readonly TOption[]) => React.ReactNode;
    trackName: string;

    label?: React.ReactNode;
    value?: TValue;
    defaultValue?: TValue;
    onChange?: (value: TValue, option: TOption) => void;

    /**
     * @deprecated label size should be based on Radio group defined styling. This is no longer used internally.
     *
     * Use `radioSize` to define radio sizing.
     */
    optionLabelSize?: TTypographyVariant;
    /**
     * @deprecated helper text size should be based on Radio group defined styling. This is no longer used internally.
     *
     * Use `radioSize` to define radio sizing.
     */
    optionHelperTextSize?: TTypographyVariant;
    radioSize?: TCheckboxSize;
}

const RadioGroup = <TOption, TValue extends string = string>({
    label,
    options,
    getOptionValue,
    defaultValue,
    value: propValue,
    onChange,
    getOptionLabel,
    getHelperText,
    radioSize = "sm",

    ...props
}: IProps<TOption, TValue>) => {
    const [stateValue, setValue] = React.useState<TValue | null>(defaultValue ?? null);
    const value: TValue | null = typeof propValue === "string" ? propValue : stateValue;

    return (
        <Flex column spacing={1.5} {...props}>
            {label && (
                <Typography variant="label-small-medium" color="text-primary-1200">
                    {label}
                </Typography>
            )}
            {options.map((option, index) => {
                const optionValue = getOptionValue(option, index, options);
                const optionLabel = getOptionLabel(option, index, options);
                const helperText = getHelperText?.(option, index, options);

                return (
                    <Radio
                        key={optionValue}
                        checked={value === optionValue}
                        onChange={checked => {
                            if (checked) {
                                setValue(optionValue);
                                onChange?.(optionValue, option);
                            }
                        }}
                        label={optionLabel}
                        helperText={helperText}
                        size={radioSize}
                        trackName="option"
                        trackData={{ value: optionValue }}
                    />
                );
            })}
        </Flex>
    );
};

export default RadioGroup;
